import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputBase,
  CircularProgress,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Search,
  FileText,
  Eye,
  Send,
  CalendarDays,
  UserRound,
  X,
} from "lucide-react";

const PrescriptionsDialog = ({
  openPrescriptions,
  setOpenPrescriptions,
  patient,
}) => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchPrescriptions = async () => {
    if (!patient?._id) return;

    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/prescriptions/client/${patient._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = res?.data?.data || [];

      setPrescriptions(data);
      setFiltered(data);
    } catch (err) {
      console.error("Fetch prescriptions error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (openPrescriptions) {
      fetchPrescriptions();
    }
  }, [openPrescriptions]);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(prescriptions);
      return;
    }

    const lower = search.toLowerCase();

    const result = prescriptions.filter((prescription) => {
      return (
        prescription?.diagnosis?.toLowerCase()?.includes(lower) ||
        prescription?.doctorName?.toLowerCase()?.includes(lower) ||
        prescription?.drugs?.some((drug) =>
          String(drug || "")
            .toLowerCase()
            .includes(lower)
        )
      );
    });

    setFiltered(result);
  }, [search, prescriptions]);

  const totalDrugs = useMemo(() => {
    return prescriptions.reduce(
      (sum, item) => sum + Number(item?.drugs?.length || 0),
      0
    );
  }, [prescriptions]);

  return (
    <Dialog
      open={openPrescriptions}
      onClose={() => setOpenPrescriptions(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e2e8f0",
          px: 3,
          py: 2.5,
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold tracking-[-0.03em] text-slate-950">
              Prescriptions
            </h2>

            <p className="mt-1 text-sm font-normal text-slate-500">
              {patient?.name || "Patient"} · {prescriptions.length} record
              {prescriptions.length === 1 ? "" : "s"}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setOpenPrescriptions(false)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50"
          >
            <X size={16} />
          </button>
        </div>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Stats */}
        <div className="mb-5 grid grid-cols-2 gap-3 mt-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs text-slate-500">Total prescriptions</p>
            <p className="mt-2 text-xl font-semibold text-slate-950">
              {prescriptions.length.toLocaleString()}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs text-slate-500">Total drugs</p>
            <p className="mt-2 text-xl font-semibold text-slate-950">
              {totalDrugs.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-5">
          <label className="mb-2 block text-xs font-medium text-slate-500">
            Search
          </label>

          <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 transition focus-within:border-slate-400 focus-within:ring-4 focus-within:ring-slate-100">
            <Search size={16} className="text-slate-400" />

            <InputBase
              placeholder="Diagnosis, drug, or doctor"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                fontSize: "14px",
                color: "#0f172a",
                "& input::placeholder": {
                  color: "#94a3b8",
                  opacity: 1,
                },
              }}
            />
          </div>
        </div>

        {/* List */}
        <div className="max-h-[420px] overflow-y-auto pr-1">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <CircularProgress size={24} sx={{ color: "#0f172a" }} />
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <FileText size={20} className="text-slate-500" />
              </div>

              <h3 className="text-sm font-semibold text-slate-950">
                No prescriptions found
              </h3>

              <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-slate-500">
                Try changing your search term or check again later.
              </p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="space-y-3">
              {filtered.map((prescription, index) => (
                <PrescriptionItem
                  key={prescription._id || index}
                  prescription={prescription}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid #e2e8f0",
          px: 3,
          py: 2,
        }}
      >
        <button
          type="button"
          onClick={() => setOpenPrescriptions(false)}
          className="h-10 rounded-xl border border-slate-200 bg-white px-5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          Close
        </button>
      </DialogActions>
    </Dialog>
  );
};

const PrescriptionItem = ({ prescription, index }) => {
  const drugs = prescription.drugs || [];
  const visibleDrugs = drugs.slice(0, 3);
  const extraDrugs = drugs.length - visibleDrugs.length;

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-slate-300 hover:shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
          {index + 1}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h4 className="truncate text-sm font-semibold text-slate-950">
                {prescription.diagnosis || "No diagnosis"}
              </h4>

              <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                <UserRound size={13} />
                <span className="truncate">
                  {prescription.doctorName || "Zaviramed Doctor"}
                </span>
              </p>
            </div>

            <p className="flex shrink-0 items-center gap-1 text-xs text-slate-400">
              <CalendarDays size={13} />
              {prescription.createdAt
                ? new Date(prescription.createdAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "—"}
            </p>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">
              Drugs
            </p>

            {visibleDrugs.length ? (
              <div className="flex flex-wrap gap-2">
                {visibleDrugs.map((drug, idx) => (
                  <span
                    key={idx}
                    className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200"
                  >
                    {drug}
                  </span>
                ))}

                {extraDrugs > 0 && (
                  <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
                    +{extraDrugs} more
                  </span>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No drugs listed.</p>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {prescription.pdfUrl && (
              <button
                type="button"
                onClick={() => window.open(prescription.pdfUrl, "_blank")}
                className="inline-flex h-9 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
              >
                <Eye size={14} />
                View PDF
              </button>
            )}

            <ResendButton prescriptionId={prescription._id} />
          </div>
        </div>
      </div>
    </article>
  );
};

const ResendButton = ({ prescriptionId }) => {
  const [loading, setLoading] = useState(false);

  const handleResend = async () => {
    try {
      setLoading(true);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/prescriptions/resend/${prescriptionId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      alert("Prescription resent successfully");
    } catch (err) {
      console.error("Resend error:", err);
      alert(err?.response?.data?.message || "Failed to resend");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleResend}
      disabled={loading}
      className={`inline-flex h-9 items-center gap-2 rounded-xl px-3 text-xs font-medium transition ${
        loading
          ? "cursor-not-allowed bg-slate-100 text-slate-400"
          : "bg-[#075E54] text-white hover:bg-[#064b43]"
      }`}
    >
      {loading ? (
        <>
          <CircularProgress size={13} color="inherit" />
          Sending
        </>
      ) : (
        <>
          <Send size={14} />
          Resend
        </>
      )}
    </button>
  );
};

export default PrescriptionsDialog;